import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { concat, defer, EMPTY, finalize, interval, map, takeWhile, tap } from 'rxjs';
import { ModalComponent, ModalData } from 'shared/src/components/modal/modal.component';
import { UserReview } from 'shared/src/components/user-review/user-review.component';
import { PROJECT_NAME, ProjectName, ProjectNames } from 'shared/src/tokens';
import { AnalyticsService } from '../../../../../shared/src/services/analytics.service';
import { fadeAnimation } from '../../animations/route-animations';
import { preparationReviews } from '../../data/reviews';
import { injectScreenIndex } from '../../flow/flow-routing';
import { FunnelFlowService } from '../../services/funnel-flow.service';

interface ProgressSection {
  id: number;
  label: string;
  value: number;
  displayValue: number;
  progressBarClass: string;
  completeValueClass: string;
}

@Component({
  selector: 'app-funnel-preparation',
  templateUrl: './preparation.component.html',
  styleUrls: ['./preparation.component.scss'],
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatProgressBarModule],
  animations: [fadeAnimation],
})
export class PreparationComponent implements OnInit, OnDestroy, AfterViewInit {
  // Progress sections data
  progressSections: ProgressSection[] = [
    {
      id: 0,
      label: $localize`:@@responsesAnalysed:Responses are being analysed`,
      value: 0,
      displayValue: 0,
      progressBarClass: 'mat-warn',
      completeValueClass: 'yellow-color',
    },
    {
      id: 1,
      label: $localize`:@@selectingVoices:Selecting the voices`,
      value: 0,
      displayValue: 0,
      progressBarClass: 'mat-accent',
      completeValueClass: 'blue-color',
    },
    {
      id: 2,
      label: $localize`:@@addingBooks:Adding the books`,
      value: 0,
      displayValue: 0,
      progressBarClass: 'tealish-color',
      completeValueClass: 'tealish-color',
    },
    {
      id: 3,
      label: $localize`:@@creatingAccount:Creating account for you`,
      value: 0,
      displayValue: 0,
      progressBarClass: '',
      completeValueClass: 'main-color',
    },
  ];

  userReviews: UserReview[] = preparationReviews;

  isReaderFlow = false;
  private isShortFlow = false;
  private readonly screenIndex = injectScreenIndex();

  // Progress segment boundaries (where each section starts/ends in the overall progress)
  private readonly segmentBoundaries = [0, 30, 57, 77, 100];
  private isActive = true;
  private duration = Math.random() * 8000 + 10000;
  private intervalDuration = 16; // 60fps

  constructor(
    private flowService: FunnelFlowService,
    private dialog: MatDialog,
    private analyticsService: AnalyticsService,
    @Inject(PROJECT_NAME) projectName: ProjectName
  ) {
    this.isReaderFlow = projectName === ProjectNames.READER_FUNNEL;
    this.isShortFlow =
      projectName === ProjectNames.GOOGLE_FUNNEL || projectName === ProjectNames.SHORT_FUNNEL;

    if (this.isReaderFlow) {
      this.progressSections = [
        {
          id: 0,
          label: $localize`:@@readerPreparationProfile:Profile`,
          value: 0,
          displayValue: 0,
          progressBarClass: 'mat-warn',
          completeValueClass: 'yellow-color',
        },
        {
          id: 1,
          label: $localize`:@@readerPreparationGoals:Setting goals`,
          value: 0,
          displayValue: 0,
          progressBarClass: 'mat-accent',
          completeValueClass: 'blue-color',
        },
        {
          id: 2,
          label: $localize`:@@readerPreparationPreferences:Adapting preferences`,
          value: 0,
          displayValue: 0,
          progressBarClass: 'tealish-color',
          completeValueClass: 'tealish-color',
        },
        {
          id: 3,
          label: $localize`:@@readerPreparationPlan:Creating your plan`,
          value: 0,
          displayValue: 0,
          progressBarClass: '',
          completeValueClass: 'main-color',
        },
      ];
    }

    // Shorter preparation screen for Google flow
    if (this.isShortFlow) {
      this.duration = Math.random() * 5000 + 6000;
    }
  }

  ngOnInit(): void {
    const steps = this.isReaderFlow
      ? [
          {
            toValue: 25,
            question: {
              title: $localize`:@@readerPreparationMemoryQuestion:Do you find it hard to remember information after reading?`,
              message: $localize`:@@toMoveForwardSpecify:To move forward, specify`,
            },
          },
          {
            toValue: 70,
            question: {
              title: $localize`:@@readerPreparationLearningQuestion:Do ADHD, dyslexia, or similar learning differences affect how you read?`,
              message: $localize`:@@toMoveForwardSpecify:To move forward, specify`,
            },
          },
          {
            toValue: 90,
            question: {
              title: $localize`:@@readerPreparationSpeedQuestion:Do you ever feel like you could read faster?`,
              message: $localize`:@@toMoveForwardSpecify:To move forward, specify`,
            },
          },
          { toValue: 100, question: null },
        ]
      : [
          {
            toValue: 25,
            question: {
              title: $localize`:@@processInfoListening:Do you process information well through listening?`,
              message: $localize`:@@toMoveForwardSpecify:To move forward, specify`,
            },
          },
          {
            toValue: 70,
            question: {
              title: $localize`:@@addHandPickedBooks:Should we add a few hand-picked books to your library?`,
              message: $localize`:@@toMoveForwardSpecify:To move forward, specify`,
            },
          },
          {
            toValue: 90,
            question: {
              title: $localize`:@@needFriendlyPush:Do you sometimes need a friendly push to keep moving forward?`,
              message: $localize`:@@toMoveForwardSpecify:To move forward, specify`,
            },
          },
          { toValue: 100, question: null },
        ];

    let previousValue = 0;

    const stepObservables = steps.map(step => {
      const progressDifference = step.toValue - previousValue;
      const stepDuration = (progressDifference / 100) * this.duration;

      const progress$ = this.createProgressObservable(previousValue, step.toValue, stepDuration);

      previousValue = step.toValue;

      // Skip question dialogs for Google funnel to keep it fast
      if (step.question && !this.isShortFlow) {
        // Run progress animation, then show question modal, and only continue after user responds
        return concat(
          progress$,
          defer(() => this.askQuestion(step.question.title, step.question.message))
        );
      } else {
        return progress$;
      }
    });

    concat(...stepObservables)
      .pipe(
        takeWhile(() => this.isActive),
        finalize(() => {
          if (this.isActive) {
            // Ensure all bars reach 100% at the end
            this.updateAllProgressValues(100);

            setTimeout(() => {
              this.analyticsService.track('web_customization_screen_viewed');
              this.flowService.handleFinishedScreen(this.screenIndex);
            }, 1000);
          }
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.analyticsService.track('web_customization_screen_view');
  }

  ngOnDestroy(): void {
    this.isActive = false;
  }

  getCompleteClass(section: ProgressSection): { [key: string]: boolean } {
    return {
      [section.completeValueClass]: section.displayValue >= 99,
    };
  }

  private updateAllProgressValues(overallValue: number): void {
    // Update each section based on its segment boundaries
    this.progressSections.forEach((section, index) => {
      const start = this.segmentBoundaries[index];
      const end = this.segmentBoundaries[index + 1];

      if (overallValue <= start) {
        section.value = 0;
      } else if (overallValue >= end) {
        section.value = 100;
      } else {
        section.value = ((overallValue - start) / (end - start)) * 100;
      }

      // Update the display value
      section.displayValue = Math.round(section.value);
    });
  }

  private createProgressObservable(startValue: number, endValue: number, duration: number) {
    return interval(this.intervalDuration).pipe(
      takeWhile(() => this.isActive),
      map(elapsedIntervals => {
        const elapsed = (elapsedIntervals + 1) * this.intervalDuration;
        const t = Math.min(elapsed / duration, 1);
        const easedProgress = this.cubicEaseOut(t);

        // Calculate current progress value (0-100 scale)
        const overallValue = startValue + easedProgress * (endValue - startValue);

        // Ensure we reach exactly endValue when animation completes
        return t === 1 ? endValue : overallValue;
      }),
      tap(overallValue => this.updateAllProgressValues(overallValue)),
      takeWhile(currentValue => currentValue < endValue, true)
    );
  }

  private askQuestion(title: string, message: string) {
    if (!this.isActive) {
      return EMPTY;
    }

    return this.dialog
      .open<ModalComponent, ModalData>(ModalComponent, {
        disableClose: true,
        data: {
          title,
          message,
          okButtonTitle: $localize`:@@yes:Yes`,
          cancelButtonTitle: $localize`:@@no:No`,
          isConfirmation: true,
        },
      })
      .afterClosed()
      .pipe(
        map(isYes => {
          this.analyticsService.track('web_customization_question_responded', {
            positive: isYes,
            question: title,
          });

          return isYes;
        })
      );
  }

  private cubicEaseOut(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }
}
